import { useRef } from 'react';
import dayjs from 'dayjs';

import { useDTicketSubscription } from '@mopla/business-logic';
import { ESubscriptionStatus } from '@mopla/data-models';

import { getCancellationCase, getCancellationDates } from '../helpers';
import { ECancellationCase, ICancellationDates } from '../types';

interface ICapturedCancellationData {
	cancellationCase: ECancellationCase;
	dates: ICancellationDates;
	isPauseEndingSoon: boolean;
	hasPause: boolean;
	isInvalid: boolean;
	hasPendingCancel: boolean;
	disallowCancelCancellation: boolean;
}

export const useDTicketSubscriptionCancellationData = () => {
	const { subscription, isSubscriptionLoading } = useDTicketSubscription();
	const initialDataRef = useRef<ICapturedCancellationData | null>(null);

	if (!initialDataRef.current && subscription) {
		const cancellationCase = getCancellationCase(subscription);
		if (cancellationCase) {
			const dates = getCancellationDates(subscription);
			// Check if the pause will end before next month starts
			// (i.e., the subscription will be active again next month)
			const isPauseEndingSoon = subscription.pauseEnd
				? !dayjs(subscription.pauseEnd).isAfter(dayjs().endOf('month'))
				: false;
			// Subscription is paused or has a scheduled pause.
			const hasPause = !!subscription.pauseStart;
			// Subscription is invalid (e.g. failed payment).
			const isInvalid = subscription.status === ESubscriptionStatus.INVALID;
			// Subscription has a scheduled cancellation.
			const hasPendingCancel = !!subscription.canceledAt;
			// Backend forbids revoking the cancellation (e.g. cancelled by an admin or failed payment).
			const { disallowCancelCancellation } = subscription;
			initialDataRef.current = {
				cancellationCase,
				dates,
				isPauseEndingSoon,
				hasPause,
				isInvalid,
				hasPendingCancel,
				disallowCancelCancellation,
			};
		}
	}

	return {
		isLoading: isSubscriptionLoading && !initialDataRef.current,
		cancellationCase: initialDataRef.current?.cancellationCase,
		dates: initialDataRef.current?.dates,
		isPauseEndingSoon: initialDataRef.current?.isPauseEndingSoon ?? false,
		hasPause: initialDataRef.current?.hasPause ?? false,
		isInvalid: initialDataRef.current?.isInvalid ?? false,
		hasPendingCancel: initialDataRef.current?.hasPendingCancel ?? false,
		disallowCancelCancellation:
			initialDataRef.current?.disallowCancelCancellation ?? false,
	};
};
